import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Country} from '../../../../shared/models/res/country';
import {CountriesEnum} from '../../../../shared/enums/res/Countries';
import {State} from '../../../../shared/models/res/state';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {LicenseInfo} from '../../../../shared/models/accr/LicenseInfo';
import {LocationService} from '../../../../shared/services/location.service';
import {MessageInfo} from '../../../../shared/enums/error-messages/MessageInfo';
import {TooltipService} from '../../../../shared/services/tooltip.service';
import {reactiveFormValidator} from '../../../../shared/utils/Reactive-form-validation-trigger';
import {ConfirmModalComponent} from '../confirm-modal/confirm-modal.component';
import {Attachment} from '../../../../shared/models/res/attachment';
import {ApiEndpoints} from '../../../../shared/constants/ApiEndpoints';
import {environment} from '../../../../../environments/environment';
import {HttpClient, HttpEventType} from '@angular/common/http';
import {ImageUrls} from '../../../../shared/constants/ImageUrls';
import {GeneralConstant} from '../../../../shared/constants/GeneralConstant';
import {Util} from '../../../../shared/utils/Util';

@Component({
  selector: 'app-license-info-form',
  templateUrl: './license-info-form.component.html',
  styleUrls: ['./license-info-form.component.scss']
})
export class LicenseInfoFormComponent implements OnInit {

  public formGroup: FormGroup;
  public licenseInfo: LicenseInfo;
  public file = null;

  // File icon.
  fileIcon = ImageUrls.ACCREDITATION.FILE_ICON;
  // File upload icon.
  fileUploadIcon = ImageUrls.ACCREDITATION.FILE_UPLOAD_ICON;
  // Accepted Files.
  acceptedFiles = GeneralConstant.ACCEPTED_ACCREDITATION_FILES;

  // Today date.
  dateNow = new Date();
  // Prefix USA
  prefix = {
    id: 235,
    code: 'US',
    phoneCode: '1'
  };
  dateClosedMin = new Date();
  /**
   * Minimum of End Date
   */
  endDateMin: Date;
  /**
   * Minimum of Expiration Date
   */
  expirationDateMin: Date;
  // Countries
  countries: Country[] = [];
  // States
  states: State[] = [];
  defaultInvalid = MessageInfo.LIMIT_2_AND_100;
  defaultRequired = MessageInfo.REQUIRED_FIELD;
  defaultExists = MessageInfo.ALREADY_EXISTS;
  invalidEmail = MessageInfo.LIMIT_6_AND_100;
  invalidAddress = MessageInfo.LIMIT_1_AND_255;
  invalidCity = MessageInfo.LIMIT_1_AND_100;
  invalidUsername = MessageInfo.LIMIT_5_AND_100;
  tooltipService: any;

  constructor(public dialogRef: MatDialogRef<LicenseInfoFormComponent>,
              private formBuilder: FormBuilder,
              private locationService: LocationService,
              public matDialog: MatDialog,
              @Inject(MAT_DIALOG_DATA) public data: any,
              private httpClient: HttpClient) {
    this.getCountries();
    this.getStatesByCountryId(CountriesEnum.USA);
  }

  ngOnInit(): void {
    this.file = this.licenseInfo.attachment;
    this.initForm(this.licenseInfo);
  }

  private getDateString(dateString: string): string {
    return new Date(dateString).toISOString().split('T')[0]; // Extract YYYY-MM-DD
  }

  fieldModified(fieldName: string): boolean {
    const licenseNumber = this.licenseInfo?.id;
    const fieldValue = this.licenseInfo[fieldName];
    const originalLicense = this.data?.originalLicenses?.find((it: LicenseInfo) => it.id === licenseNumber);
    if (['countryIssued', 'stateIssued'].includes(fieldName)) {
      return !originalLicense && fieldValue ? true : originalLicense ? originalLicense[fieldName]?.id !== fieldValue?.id : false;
    }
    if (['effectiveDate', 'expirationDate'].includes(fieldName)) {
      return !originalLicense && fieldValue ? true : originalLicense[fieldName] ? this.getDateString(originalLicense[fieldName]) !== this.getDateString(fieldValue) : false;
    }
    return !originalLicense && fieldValue ? true : originalLicense[fieldName] ? originalLicense[fieldName] !== fieldValue : false;
  }

  private initForm(licenseInfo?: LicenseInfo) {
    this.formGroup = this.formBuilder.group({
      licenseNumber: new FormControl(licenseInfo?.licenseNumber, Validators.required),
      countryIssued: new FormControl(licenseInfo?.countryIssued?.id ?? this.prefix.id, Validators.required),
      stateIssued: new FormControl({
        value: licenseInfo?.stateIssued?.id,
        disabled: (licenseInfo?.countryIssued?.id ?? this.prefix.id) !== this.prefix.id
      }, Validators.required),
      expirationDate: new FormControl(licenseInfo?.expirationDate, Validators.required),
      effectiveDate: new FormControl(licenseInfo?.effectiveDate, Validators.required),
      attachment: new FormControl(licenseInfo?.attachment, Validators.required)
    });
    this.tooltipService = new TooltipService(this.formGroup);

    if (this.formGroup.get('effectiveDate').value) {
      this.onEffectiveDateChange(this.formGroup.get('effectiveDate').value);
    }

    if (this.data?.disableForm) {
      this.formGroup.disable();
    }
  }

  /**
   * Get country data.
   */
  getCountries() {
    this.locationService.getCountries().subscribe(
      result => {
        if (result) {
          this.countries = result;
        }
      }
    );
  }

  /**
   * Get States by country Id.
   * @param countryId - country id
   */
  private getStatesByCountryId(countryId) {
    this.locationService.getStates(CountriesEnum.USA).subscribe(result => {
      this.states = result;
    });
  }

  /**
   * Defines minimum of Expiration Date Field on change of Effective Date Field
   * @param effectiveDate s
   */
  onEffectiveDateChange(effectiveDate) {
    this.expirationDateMin = new Date(effectiveDate);
    this.expirationDateMin.setDate(this.expirationDateMin.getDate() + 1);
  }

  public staffCountrySelected(event) {
    if (event.value === CountriesEnum.USA) {
      this.formGroup.get('stateIssued').enable();
    } else {
      this.formGroup.get('stateIssued').setValue(null);
      this.formGroup.get('stateIssued').disable();
    }
  }

  /**
   * Cancel
   */
  cancel() {
    if (this.formGroup.dirty) {
      const dialogRef = this.matDialog.open(ConfirmModalComponent, {
        data: {
          fullName: 'Warning',
          name: '',
          type: 'Confirm',
          removeQuestionMark: true,
          content: 'You have unsaved changes. If you close the pop-up window, these changes will be lost.'
        }
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result && result.delete) {
          this.dialogRef.close();
        }
        return;
      });
    } else {
      this.dialogRef.close();
    }
  }

  /**
   * Save item.
   */
  onSave(event) {
    if (this.formGroup.invalid) {
      event.onComplete();
      reactiveFormValidator(this.formGroup);
      return;
    }
    const item = this.formGroup.getRawValue();
    this.licenseInfo.licenseNumber = item.licenseNumber;
    this.licenseInfo.countryIssued = item.countryIssued ? this.countries.find(x => x.id === item.countryIssued) : null;
    this.licenseInfo.stateIssued = item.stateIssued ? this.states.find(x => x.id === item.stateIssued) : null;
    this.licenseInfo.expirationDate = Util.addCurrentTimeToDate(item.expirationDate);
    this.licenseInfo.effectiveDate = Util.addCurrentTimeToDate(item.effectiveDate);
    this.licenseInfo.attachment = item.attachment;
    this.dialogRef.close(this.licenseInfo);
  }

  close() {
    this.dialogRef.close();
  }

  fileClicked(file) {
    const link = file.filePath;
    if (link) {
      window.open(link, '_blank');
    }
  }

  onFileAdd(event) {
    const addedFile = event.addedFiles[0];

    const attachment = new Attachment();
    attachment.name = addedFile.name;
    attachment.size = addedFile.size;
    (attachment as any).progress = 0;
    const formData = new FormData();
    formData.append('file', addedFile, addedFile.name);
    this.httpClient.post(
      `${environment.api}/${environment.apiVersion}/${ApiEndpoints.FILE.FILE}/create/single_upload`,
      formData,
      {reportProgress: true, observe: 'events'}
    ).subscribe(ev => {
      if (ev.type === HttpEventType.UploadProgress) {
        (attachment as any).progress = Math.round(100 * ev.loaded / attachment.size);
      }
      if (ev.type === HttpEventType.Response) {
        this.licenseInfo.attachment = (ev.body as any).data;
        this.file = this.licenseInfo.attachment;

        this.formGroup.patchValue({ attachment: this.licenseInfo.attachment });
        this.formGroup.get('attachment').updateValueAndValidity();
      }
    });
  }

  onRemoveFile() {
    this.file = null;
    this.licenseInfo.attachment = null;

    this.formGroup.patchValue({ attachment: null });
    this.formGroup.get('attachment').updateValueAndValidity();
  }
}
